import React, {ReactNode, useEffect, useRef, useState} from 'react';

import FocusLock from 'react-focus-lock';

import {Root, Paranja, Content} from './styled';

interface Vector2 {
    x: number;
    y: number;
}

interface KeyboardEvent {
    key: string;
    stopPropagation: Function;
}

const calculatePivotPosition = (
    pivotElementRef: Element | undefined,
    contentRef: Element | null,
    offset: Vector2,
    checkInnerWindowHeight = false,
): Vector2 => {
    if (pivotElementRef == null) {
        return {x: 0, y: 0};
    }

    let {left: parentX, top: parentY} = pivotElementRef.getBoundingClientRect();
    parentX -= offset.x;
    parentY -= offset.y;

    const restX = window.innerWidth - parentX;
    const restY = checkInnerWindowHeight ? window.innerHeight - parentY : window.innerHeight;

    const {width: contentWidth, height: contentHeight} = contentRef
        ? contentRef.getBoundingClientRect()
        : {width: 0, height: 0};
    return {
        x: restX > contentWidth ? parentX : parentX - (contentWidth - restX),
        y: restY > contentHeight ? parentY : parentY - (contentHeight - restY),
    };
};

interface Props {
    isOpen: boolean;
    onClose: () => void;
    pivotElement?: Element;
    offset?: Vector2;
    checkInnerWindowHeight?: boolean;
    children: ReactNode;
    className?: string;
}

export const Popover = ({
    isOpen,
    onClose,
    pivotElement,
    offset = {x: 0, y: 0},
    checkInnerWindowHeight = false,
    children,
    className,
}: Props) => {
    const contentRef = useRef(null);

    const [contentPosition, setContentPosition] = useState<Vector2>({x: 0, y: 0});

    const setPosition = () => {
        setContentPosition(
            calculatePivotPosition(
                pivotElement,
                contentRef.current,
                offset,
                checkInnerWindowHeight,
            ),
        );
    };

    useEffect(() => {
        setPosition();
        let parentPositionInterval: NodeJS.Timeout;
        if (isOpen) {
            parentPositionInterval = setInterval(() => {
                setPosition();
            }, 60);
        }
        return () => clearInterval(parentPositionInterval);
    }, [isOpen, pivotElement]);

    return isOpen ? (
        <Root
            onKeyDown={(evt: KeyboardEvent) => {
                if (evt.key === 'Escape') {
                    evt.stopPropagation();
                    onClose();
                }
            }}
        >
            <FocusLock>
                <Paranja onClick={onClose} />
                <Content
                    ref={contentRef}
                    style={{
                        left: contentPosition.x,
                        top: contentPosition.y,
                    }}
                    className={className}
                >
                    {children}
                </Content>
            </FocusLock>
        </Root>
    ) : null;
};
